import { useEffect, useMemo, useState } from "react";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { Link, useNavigate, useLocation } from "react-router-dom";

function formatDate(dateStr) {
  if (!dateStr) return "-";
  const [datePart, timePart] = dateStr.split("T");
  const [year, month, day] = datePart.split("-");
  return `${day}.${month}.${year} kl. ${timePart}`;
}

const fmt = (n) => Number(n || 0).toLocaleString("no-NO");

// --- Tid/pris-hjelpere ---
function diffHours(from, to) {
  const a = new Date(from);
  const b = new Date(to);
  return Math.max(0, (b.getTime() - a.getTime()) / 36e5);
}
function ceilDays(from, to) {
  const h = diffHours(from, to);
  return Math.max(1, Math.ceil(h / 24));
}
function isWeekendRange(from, to) {
  const a = new Date(from);
  const b = new Date(to);
  const fri = a.getDay() === 5 && a.getHours() >= 16; // fre fra 16
  const sun = b.getDay() === 0 && b.getHours() <= 22; // søn til 22
  return fri && sun;
}
function hasRental(p) {
  return p && (p.price_day != null || p.price_weekend != null || p.price_short_4h != null);
}
function calcRentalPriceForPeriod(p, from, to) {
  const hours = diffHours(from, to);
  if (p.price_short_4h != null && hours > 0 && hours <= 4.01) {
    return { total: Number(p.price_short_4h), label: `Leie: ${fmt(p.price_short_4h)} kr (<4t)` };
  }
  if (p.price_weekend != null && isWeekendRange(from, to)) {
    return { total: Number(p.price_weekend), label: `Leie: ${fmt(p.price_weekend)} kr (helg)` };
  }
  if (p.price_day != null) {
    const d = ceilDays(from, to);
    const sum = Number(p.price_day) * d;
    return { total: sum, label: `Leie: ${fmt(p.price_day)} kr/døgn × ${d} døgn = ${fmt(sum)} kr` };
  }
  return { total: 0, label: "Leie: (mangler pris)" };
}
const near = (a, b, eps = 0.01) => Math.abs(Number(a ?? 0) - Number(b ?? 0)) < eps;

// Finn hvilken priskategori som ble brukt for base-linja
function detectBaseKind(baseLine, baseProduct) {
  if (!baseProduct) return null;
  const from = baseLine.date_from;
  const to   = baseLine.date_to;
  const d    = ceilDays(from, to);
  const shortPrice   = baseProduct.price_short_4h != null ? Number(baseProduct.price_short_4h) : null;
  const weekendPrice = baseProduct.price_weekend  != null ? Number(baseProduct.price_weekend)  : null;
  const dayTotal     = baseProduct.price_day      != null ? Number(baseProduct.price_day) * d  : null;

  const used = Number(baseLine.price || 0);

  if (shortPrice   != null && near(used, shortPrice)) return 'short';
  if (weekendPrice != null && near(used, weekendPrice)) return 'weekend';
  if (dayTotal     != null && near(used, dayTotal))     return 'day';

  const h = diffHours(from, to);
  if (h <= 4.01 && baseProduct.price_short_4h != null) return 'short';
  if (isWeekendRange(from, to) && baseProduct.price_weekend != null) return 'weekend';
  if (baseProduct.price_day != null) return 'day';
  return null;
}

// pricing til backend
function inferPricingKind(item) {
  if (!item?.date_from || !item?.date_to) return 'sale';
  const hours = diffHours(item.date_from, item.date_to);
  if (hours > 0 && hours <= 4.01) return 'short';
  if (isWeekendRange(item.date_from, item.date_to)) return 'weekend';
  return 'day';
}
function inferPricingUnits(item) {
  if (!item?.date_from || !item?.date_to) return item?.quantity ?? 1;
  const hours = diffHours(item.date_from, item.date_to);
  if (hours > 0 && hours <= 4.01) return 1;
  return ceilDays(item.date_from, item.date_to);
}

export default function CartPage() {
  const { cart, totalPrice, removeFromCart, clearCart, addToCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const API = process.env.REACT_APP_API_URL;

  // Vipps varsel fra query
  const qs = new URLSearchParams(location.search);
  const vippsState  = qs.get('vipps');       // cancelled / expired / rejected ...
  const vippsError  = qs.get('vipps_error'); // capture_failed / status_failed / ...

  // Betaling & vilkår
  const [payments, setPayments] = useState([]);
  const [paymentId, setPaymentId] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [errMsg, setErrMsg] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Quick Add
  const [allProducts, setAllProducts] = useState([]);
  const [suggestions, setSuggestions] = useState({});
  const [loadingSug, setLoadingSug] = useState(false);

  // Leielinjer i kurv
  const rentalLines = useMemo(() => {
    return cart
      .filter(i => i.date_from && i.date_to)
      .map(i => ({
        key: `${i.id}|${i.date_from}|${i.date_to}`,
        productId: i.id,
        name: i.name,
        price: i.price,
        date_from: i.date_from,
        date_to: i.date_to,
      }));
  }, [cart]);

  // Alle produkter
  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        const res = await fetch(`${API}/products`);
        const data = res.ok ? await res.json() : [];
        if (!ignore) setAllProducts(Array.isArray(data) ? data : []);
      } catch {
        if (!ignore) setAllProducts([]);
      }
    })();
    return () => { ignore = true; };
  }, [API]);

  // Betalingsalternativer
  useEffect(() => {
    fetch(`${API}/payments`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((list) => {
        setPayments(list || []);
        const vipps = (list || []).find((p) => /vipps/i.test(p.name || ""));
        const first = (list || [])[0];
        setPaymentId(String(vipps?.id ?? first?.id ?? ""));
      })
      .catch(() => setPayments([]));
  }, [API]);

  function isAlreadyInCart(child, base) {
    if (!child) return false;
    if (hasRental(child)) {
      return cart.some(i =>
        i.id === child.id &&
        i.date_from === base.date_from &&
        i.date_to === base.date_to
      );
    }
    return cart.some(i => i.id === child.id && !i.date_from && !i.date_to);
  }

  // Forslag pr leielinje
  useEffect(() => {
    let ignore = false;
    if (rentalLines.length === 0) { setSuggestions({}); return; }

    (async () => {
      setLoadingSug(true);
      const next = {};
      try {
        for (const base of rentalLines) {
          const headers = user?.token ? { Authorization: `Bearer ${user.token}` } : {};
          const relRes = await fetch(`${API}/addons?base_product_id=${base.productId}`, { headers });
          const relList = relRes.ok ? await relRes.json() : [];

          const baseProduct = allProducts.find(p => p.id === base.productId);
          const baseKind = detectBaseKind(base, baseProduct);

          const childProducts = relList.map(r => {
            const apiChild = r.childProduct || r.addon || null;
            const full = allProducts.find(p => p.id === (apiChild?.id ?? r.product_id)) || {};
            return apiChild ? { ...apiChild, ...full } : (Object.keys(full).length ? full : null);
          }).filter(Boolean)
            .filter(p => p.is_active !== false);

          const entries = [];
          for (const p of childProducts) {
            if (hasRental(p)) {
              let available = true;
              try {
                const avRes = await fetch(
                  `${API}/availability?productId=${p.id}&from=${encodeURIComponent(base.date_from)}&to=${encodeURIComponent(base.date_to)}`,
                  { headers }
                );
                const av = avRes.ok ? await avRes.json() : { available: true };
                available = !!av.available;
              } catch {
                available = true;
              }

              let computedPrice = 0;
              let priceLabel = '';
              const days = ceilDays(base.date_from, base.date_to);

              if (baseKind === 'short' && p.price_short_4h != null) {
                computedPrice = Number(p.price_short_4h);
                priceLabel = `Leie: ${fmt(p.price_short_4h)} kr (<4t)`;
              } else if (baseKind === 'weekend' && p.price_weekend != null) {
                computedPrice = Number(p.price_weekend);
                priceLabel = `Leie: ${fmt(p.price_weekend)} kr (helg)`;
              } else if (baseKind === 'day' && p.price_day != null) {
                computedPrice = Number(p.price_day) * days;
                priceLabel = `Leie: ${fmt(p.price_day)} kr/døgn × ${days} døgn = ${fmt(computedPrice)} kr`;
              } else {
                const fb = calcRentalPriceForPeriod(p, base.date_from, base.date_to);
                computedPrice = fb.total;
                priceLabel = fb.label;
              }

              entries.push({ product: p, kind: 'rental', available, priceLabel, computedPrice });
            } else if (p.is_for_sale && p.price_sale != null) {
              entries.push({
                product: p,
                kind: 'sale',
                available: true,
                priceLabel: `Kjøp: ${fmt(p.price_sale)} kr`,
                computedPrice: Number(p.price_sale || 0),
              });
            }
          }

          next[base.key] = { baseItem: base, baseKind, suggestions: entries };
        }

        setSuggestions(next);
      } catch {
        setSuggestions({});
      } finally {
        setLoadingSug(false);
      }
    })();

    return () => { ignore = true; };
  }, [API, rentalLines, user?.token, allProducts]);

  function handleQuickAdd(entry, base) {
    if (!addToCart) return;
    const p = entry.product;

    if (entry.kind === 'rental') {
      const payload = {
        id: p.id,
        name: p.name,
        price: Number(entry.computedPrice || 0),
        date_from: base.date_from,
        date_to: base.date_to,
        quantity: 1,
        pricing_kind: inferPricingKind({ date_from: base.date_from, date_to: base.date_to }),
        pricing_units: inferPricingUnits({ date_from: base.date_from, date_to: base.date_to }),
      };
      addToCart(payload);
    } else if (entry.kind === 'sale') {
      const payload = {
        id: p.id,
        name: p.name,
        price: Number(entry.computedPrice || p.price_sale || 0),
        quantity: 1,
        pricing_kind: 'sale',
        pricing_units: 1,
      };
      addToCart(payload);
    }
  }

  // Grupper: base → eksisterende barn → knapper
  const groups = useMemo(() => {
    const usedIdx = new Set();
    const out = [];

    for (let i = 0; i < cart.length; i++) {
      if (usedIdx.has(i)) continue;
      const base = cart[i];

      const baseKey = (base.date_from && base.date_to) ? `${base.id}|${base.date_from}|${base.date_to}` : null;
      const sugg = baseKey ? suggestions[baseKey] : null;
      const childIds = (sugg?.suggestions || []).map(e => e.product.id);

      const children = [];
      for (let j = i + 1; j < cart.length; j++) {
        if (usedIdx.has(j)) continue;
        const cand = cart[j];

        const isRentalChild =
          base.date_from && base.date_to &&
          cand.date_from && cand.date_to &&
          cand.date_from === base.date_from &&
          cand.date_to === base.date_to &&
          childIds.includes(cand.id);

        const isSaleChild =
          (!cand.date_from && !cand.date_to) &&
          childIds.includes(cand.id);

        if (isRentalChild || isSaleChild) {
          children.push({ idx: j, item: cand });
          usedIdx.add(j);
        }
      }

      out.push({ baseIndex: i, base, children, baseKey, sugg });
      usedIdx.add(i);
    }

    return out;
  }, [cart, suggestions]);

  if (!cart.length) {
    return (
      <div className="max-w-xl mx-auto text-center mt-16">
        <h2 className="text-2xl mb-6 font-bold">Handlekurven er tom 🛒</h2>
        <Link to="/" className="text-blue-700 hover:underline">
          Gå tilbake til forsiden
        </Link>
      </div>
    );
  }

  async function handleCheckout() {
    setErrMsg("");

    if (!paymentId) {
      setErrMsg("Velg betalingsmåte før du går videre.");
      return;
    }
    if (!termsAccepted) {
      setErrMsg("Du må godta leievilkårene for å fortsette.");
      return;
    }

    const selectedPayment = payments.find(p => String(p.id) === String(paymentId));
    const isVipps = /vipps/i.test(selectedPayment?.name || "");

    try {
      setSubmitting(true);

      const orderPayload = {
        items: cart.map((item) => ({
          productId: item.id,
          name: item.name,
          price: item.price,
          date_from: item.date_from,
          date_to: item.date_to,
          quantity: item.quantity,
          pricing_kind: item.pricing_kind ?? inferPricingKind(item),
          pricing_units: item.pricing_units ?? inferPricingUnits(item),
        })),
        total: totalPrice,
        userId: user?.id,
        statusId: 1,
        paymentId: Number(paymentId),
      };

      if (isVipps) {
        const res = await fetch(`${API}/vipps/checkout`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(user?.token ? { Authorization: `Bearer ${user.token}` } : {}),
          },
          body: JSON.stringify(orderPayload),
        });

        const data = await res.json().catch(() => ({}));
        if (!res.ok || !data.redirectUrl) {
          throw new Error(data.error || "Kunne ikke starte Vipps-betaling");
        }
        // Viktig: ikke tøm kurv her. Vi tømmer kun etter vellykket ordre.
        window.location.href = data.redirectUrl;
        return;
      }

      // Andre betalingsmåter (ikke Vipps): lagre ordre direkte
      const res = await fetch(`${API}/orders`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(user?.token ? { Authorization: `Bearer ${user.token}` } : {}),
        },
        body: JSON.stringify(orderPayload),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || "Kunne ikke lagre ordre");
      }

      const data = await res.json();
      clearCart();
      navigate(`/order-confirmation/${data.id}`);
    } catch (err) {
      setErrMsg("Noe gikk galt: " + err.message);
      setSubmitting(false);
    }
  }

  return (
    <div className="max-w-2xl mx-auto py-12">
      <h1 className="text-3xl font-bold text-[#000099] mb-4 text-center">
        Din handlekurv
      </h1>

      {/* Vipps-varsler */}
      {(vippsState || vippsError) && (
        <div className={`mb-6 p-3 rounded border ${
          vippsError ? 'bg-red-50 border-red-300 text-red-700'
                     : 'bg-yellow-50 border-yellow-300 text-yellow-700'
        }`}>
          {vippsError
            ? `Betaling feilet: ${vippsError.replace(/_/g, ' ')}`
            : `Betaling avbrutt: ${vippsState}`}
        </div>
      )}

      {/* VARELINJER (gruppert) */}
      <ul className="divide-y divide-gray-200 mb-6">
        {groups.map((g, gi) => {
          const item = g.base;
          const lineTotal = Number(item.price || 0) * (item.quantity || 1);
          const isRental = !!(item.date_from && item.date_to);
          return (
            <li
              key={`group-${gi}-${item.id}-${item.date_from||''}-${item.date_to||''}`}
              className="py-4"
            >
              {/* BASE-LINJE */}
              <div className="flex justify-between items-center">
                <div>
                  <div className="font-medium">{item.name}</div>
                  {isRental ? (
                    <div className="text-sm text-gray-500">
                      {formatDate(item.date_from)} &rarr; {formatDate(item.date_to)}
                    </div>
                  ) : (
                    <div className="text-sm text-gray-500">Kjøp</div>
                  )}
                  {item.quantity > 1 && (
                    <div className="text-xs text-gray-500 mt-1">
                      Antall: {item.quantity}
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-3 text-right">
                  {item.quantity > 1 ? (
                    <div>
                      <div className="text-xs text-gray-500">
                        {fmt(item.price)} kr × {item.quantity}
                      </div>
                      <div className="font-bold">{fmt(lineTotal)} kr</div>
                    </div>
                  ) : (
                    <div className="font-bold">{fmt(item.price)} kr</div>
                  )}

                  <button
                    className="bg-red-500 hover:bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center"
                    onClick={() =>
                      removeFromCart(item.id, item.date_from, item.date_to)
                    }
                    title="Fjern"
                  >
                    ×
                  </button>
                </div>
              </div>

              {/* Barn som allerede er i kurv (vis FØR knappene) */}
              {g.children.length > 0 && (
                <div className="mt-3 pl-3 border-l-2 border-gray-200 space-y-2">
                  {g.children.map(({ item: child }) => {
                    const childIsRental = !!(child.date_from && child.date_to);
                    const childTotal = Number(child.price || 0) * (child.quantity || 1);
                    return (
                      <div key={`child-${child.id}-${child.date_from||''}-${child.date_to||''}`}
                           className="flex justify-between items-center bg-gray-50 px-3 py-2 rounded border">
                        <div>
                          <div className="text-sm font-medium">{child.name}</div>
                          <div className="text-xs text-gray-600">
                            {childIsRental ? 'Tilbehør (leie)' : 'Tilbehør (kjøp)'}
                          </div>
                        </div>
                        <div className="flex items-center gap-3 text-right">
                          <div className="font-medium">{fmt(childTotal)} kr</div>
                          <button
                            className="bg-red-500 hover:bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center"
                            onClick={() => removeFromCart(child.id, child.date_from, child.date_to)}
                            title="Fjern"
                          >
                            ×
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* QUICK ADD-knapper */}
              {isRental && g.sugg?.suggestions?.length > 0 && (
                <div className="mt-3 pl-3 border-l-2 border-gray-200">
                  <div className="text-sm font-medium mb-2 text-gray-700">Tilbehør</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {g.sugg.suggestions.map((entry) => {
                      const p = entry.product;
                      const already = isAlreadyInCart(p, item);
                      return (
                        <div key={`${p.id}-${entry.kind}`} className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded border">
                          <div>
                            <div className="text-sm font-medium">{p.name}</div>
                            <div className="text-xs text-gray-600">{entry.priceLabel}</div>
                            {!entry.available && <div className="text-xs text-red-600">Ikke tilgjengelig i valgt periode</div>}
                          </div>
                          <button
                            disabled={!entry.available || already}
                            onClick={() => handleQuickAdd(entry, item)}
                            className={`text-sm px-3 py-1 rounded ${
                              (!entry.available || already)
                                ? 'bg-gray-300 text-gray-600 cursor-not-allowed'
                                : 'bg-[#000099] text-white hover:bg-blue-900'
                            }`}
                          >
                            {already ? 'Lagt til' : 'Legg til'}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </li>
          );
        })}
      </ul>

      {rentalLines.length > 0 && loadingSug && (
        <div className="text-sm text-gray-500 mb-4">Laster tilbehør…</div>
      )}

      {/* Betalingsmåte */}
      <div className="mb-2">
        <label className="block mb-1 font-medium">Betalingsmåte</label>
        <select
          value={paymentId}
          onChange={(e) => setPaymentId(e.target.value)}
          className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#000099]"
        >
          <option value="">— Velg betalingsmåte —</option>
          {payments.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
      </div>
      <p className="text-xs text-gray-500 mb-6">
        Vipps åpnes i samme fane. Du kommer tilbake hit etter betaling.
      </p>

      {/* Vilkår */}
      <div className="mb-6 flex items-start gap-2">
        <input
          id="terms"
          type="checkbox"
          checked={termsAccepted}
          onChange={(e) => setTermsAccepted(e.target.checked)}
          className="mt-[3px]"
        />
        <label htmlFor="terms" className="text-sm">
          Jeg har lest og godtar{" "}
          <a
            href="/kjopsvilkar"
            target="_blank"
            rel="noopener noreferrer"
            className="underline text-[#000099] hover:text-blue-800"
          >
            kjøpsvilkår
          </a>{" "}
          og{" "}
          <a
            href="/terms"
            target="_blank"
            rel="noopener noreferrer"
            className="underline text-[#000099] hover:text-blue-800"
          >
            leievilkår
          </a>.
        </label>
      </div>

      <div className="text-right mb-4">
        <span className="font-bold text-lg">Totalpris: {fmt(totalPrice)} kr</span>
      </div>

      {errMsg && <div className="text-right text-red-600 mb-4">{errMsg}</div>}

      <div className="flex gap-4 justify-end">
        <Link
          to="/"
          className="bg-gray-200 hover:bg-gray-300 px-4 py-2 rounded text-[#000099] border border-gray-300"
        >
          Handle videre
        </Link>
        <button
          onClick={clearCart}
          className="bg-gray-300 hover:bg-gray-400 px-4 py-2 rounded text-gray-700"
        >
          Tøm handlekurv
        </button>
        <button
          onClick={handleCheckout}
          disabled={!paymentId || !termsAccepted || submitting}
          className={`font-bold py-2 px-6 rounded transition text-white ${
            !paymentId || !termsAccepted || submitting
              ? "bg-blue-300 cursor-not-allowed"
              : "bg-[#000099] hover:bg-blue-900"
          }`}
          title={
            !paymentId
              ? "Velg betalingsmåte"
              : !termsAccepted
              ? "Godta leievilkårene"
              : "Fullfør bestilling"
          }
        >
          {submitting ? "Sender…" : "Betal"}
        </button>
      </div>
    </div>
  );
}
